import { Location, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy, PLATFORM_ID, VERSION } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NAVIGATOR } from '@ng-web-apis/common';
import { Subscription, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GlitchtipService implements OnDestroy {
  private sub: Subscription = new Subscription();
  constructor(
    private httpClient: HttpClient,
    @Inject(PLATFORM_ID) private platformId: NonNullable<unknown>,
    @Inject(NAVIGATOR) private navigator: Navigator,
    private location: Location,
  ) {}

  captureMessage(error: Error, level: 'error' | 'info' | 'debug' | 'warn' = 'error') {
    if (!(error instanceof HttpErrorResponse && error.url.startsWith('https://error-logger.sharry.land/api'))) {
      const id = isPlatformBrowser(this.platformId) ? '2' : '3';
      const key = isPlatformBrowser(this.platformId) ? environment.glitchtipKey : environment.glitchtipKeyServer;
      const stack = this.getSourceLocation(error);
      const date = new Date();
      const timestampS = date.getTime().toString();
      const timestamp = timestampS.substring(0, timestampS.length - 3) + '.000';
      const capPlatform = Capacitor.getPlatform();
      const platform = capPlatform === 'web' ? 'javascript' : capPlatform;

      let framesString = '';
      stack.forEach(s => {
        framesString = framesString.concat(
          `{"filename":"${s.file}","function":"${s.function}","in_app":true,"lineno":${s.line},"colno":${s.column}},`,
        );
      });
      framesString = framesString.slice(0, -1).replace(/\s*/g, '');
      const payload = `
{"sent_at":"${date.toISOString()}","trace":{"environment":"${environment.env}","public_key":"${key}"}}
{"type":"event"}
{"message":"${error.message}","exception":{"values":[{"value":"${
        error.message
      }","stacktrace":{"frames":[${framesString}]}}]},"level":"${level}","platform":"${platform}","timestamp":${timestamp},"environment":"${
        environment.env
      }","contexts":{"angular":{"version":${
        VERSION.major
      }}},"request":{"url":"${this.location.path()}","headers":{"Referer":"${this.location.path()}","User-Agent":"${
        this.navigator.userAgent
      }"}}}
    `.trim();
      this.sub = this.httpClient
        .post(`https://error-logger.sharry.land/api/${id}/envelope/?glitchtip_key=${key}`, payload)
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }
  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
  stringify(obj: any) {
    let cache: any[] = [];
    const str = JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.indexOf(value) !== -1) {
          // Circular reference found, discard key
          return;
        }
        // Store value in our collection
        cache.push(value);
      }
      return value;
    });
    cache = null; // reset the cache
    return str;
  }

  getSourceLocation(originalError: any) {
    let error: Error;
    if (typeof originalError === 'string') {
      error = new Error(originalError);
    } else if (originalError instanceof HttpErrorResponse) {
      error = new Error(originalError.message + ' ' + this.stringify(originalError.error));
    } else {
      error = originalError;
    }
    const stacklist = error.stack
      .replace(/^.*[\\/]node_modules[\\/].*$|^.((?!at).)*$|^.*<anonymous>.*$|^.*internal\/timers.js.*$/gm, '')
      .replace(/\n+/g, '\n')
      .split('\n')
      .filter((item, index, array) => {
        if (item) {
          return index === array.indexOf(item);
        }
        return 0;
      });

    const stackReg = /at\s+(.*)\s+\((.*):(\d*):(\d*)\)/gi;
    const stackReg2 = /at\s+()(.*):(\d*):(\d*)/gi;

    const sources: { function: string; file: any; line: string; column: string }[] = [];
    stacklist.forEach(item => {
      const sp = stackReg.exec(item) || stackReg2.exec(item);
      if (sp && sp.length === 5) {
        sources.push({
          function: sp[1],
          file: sp[2],
          line: sp[3],
          column: sp[4],
        });
      }
    });
    return sources;
  }
}
